/**
 * Login View - Controller
 * =======================
 * 100% presentacional: cero auth, cero HTTP, cero routing. El estado de
 * proceso (loading/error) entra por bindings; todos los eventos salen por
 * callbacks `&?`. `email`/`password` viven acá — la app solo los recibe en
 * `onSubmit`.
 */

import type { ISCEService } from 'angular';
import { DEFAULTS, BASE_CLASSES, APPLE_LOGO, GOOGLE_LOGO } from './constants';

export interface LoginBindings {
  // Estado desde la app
  loading?: boolean;
  errorMessage?: string;
  showApple?: boolean;
  showGoogle?: boolean;
  showTerms?: boolean;

  // Textos
  title?: string;
  subtitle?: string;
  appleText?: string;
  googleText?: string;
  dividerText?: string;
  emailPlaceholder?: string;
  passwordPlaceholder?: string;
  submitText?: string;
  forgotText?: string;
  registerQuestion?: string;
  registerText?: string;
  termsPrefix?: string;
  termsLinkText?: string;
  termsConnector?: string;
  privacyLinkText?: string;

  // Callbacks
  onAppleLogin?: () => void;
  onGoogleLogin?: () => void;
  onSubmit?: (args: { email: string; password: string }) => void;
  onForgotPassword?: () => void;
  onRegister?: () => void;
  onTerms?: () => void;
  onPrivacy?: () => void;
}

export class LoginController implements LoginBindings {
  static $inject = ['$sce'];

  private $sce: ISCEService | null;

  // Bindings — estado
  loading?: boolean;
  errorMessage?: string;
  showApple?: boolean;
  showGoogle?: boolean;
  showTerms?: boolean;

  // Bindings — textos
  title?: string;
  subtitle?: string;
  appleText?: string;
  googleText?: string;
  dividerText?: string;
  emailPlaceholder?: string;
  passwordPlaceholder?: string;
  submitText?: string;
  forgotText?: string;
  registerQuestion?: string;
  registerText?: string;
  termsPrefix?: string;
  termsLinkText?: string;
  termsConnector?: string;
  privacyLinkText?: string;

  // Bindings — callbacks
  onAppleLogin?: () => void;
  onGoogleLogin?: () => void;
  onSubmit?: (args: { email: string; password: string }) => void;
  onForgotPassword?: () => void;
  onRegister?: () => void;
  onTerms?: () => void;
  onPrivacy?: () => void;

  // Estado interno (la app no lo ve hasta onSubmit)
  email = '';
  password = '';

  // SVG confiables para ng-bind-html
  trustedAppleLogo: unknown = null;
  trustedGoogleLogo: unknown = null;

  // Clases desde BASE_CLASSES
  logoClass = BASE_CLASSES.logo;

  constructor($sce: ISCEService) {
    this.$sce = $sce;
  }

  $onInit(): void {
    this.title = this.title || DEFAULTS.title;
    this.subtitle = this.subtitle || DEFAULTS.subtitle;
    this.appleText = this.appleText || DEFAULTS.appleText;
    this.googleText = this.googleText || DEFAULTS.googleText;
    this.dividerText = this.dividerText || DEFAULTS.dividerText;
    this.emailPlaceholder = this.emailPlaceholder || DEFAULTS.emailPlaceholder;
    this.passwordPlaceholder = this.passwordPlaceholder || DEFAULTS.passwordPlaceholder;
    this.submitText = this.submitText || DEFAULTS.submitText;
    this.forgotText = this.forgotText || DEFAULTS.forgotText;
    this.registerQuestion = this.registerQuestion || DEFAULTS.registerQuestion;
    this.registerText = this.registerText || DEFAULTS.registerText;
    this.termsPrefix = this.termsPrefix || DEFAULTS.termsPrefix;
    this.termsLinkText = this.termsLinkText || DEFAULTS.termsLinkText;
    this.termsConnector = this.termsConnector || DEFAULTS.termsConnector;
    this.privacyLinkText = this.privacyLinkText || DEFAULTS.privacyLinkText;

    // Bindings '<' — default DEFAULTS salvo que la app pase explícitamente false.
    this.showApple = this.showApple !== false ? DEFAULTS.showApple : false;
    this.showGoogle = this.showGoogle !== false ? DEFAULTS.showGoogle : false;
    // Footer de términos: opt-in. Oculto salvo que la app pase show-terms="true".
    this.showTerms = this.showTerms === true;

    if (this.$sce) {
      this.trustedAppleLogo = this.$sce.trustAsHtml(APPLE_LOGO);
      this.trustedGoogleLogo = this.$sce.trustAsHtml(GOOGLE_LOGO);
    }
  }

  $onDestroy(): void {
    this.$sce = null;
  }

  // ---- Eventos ----

  handleSubmit(): void {
    if (this.loading) return;
    if (typeof this.onSubmit === 'function') {
      this.onSubmit({ email: this.email, password: this.password });
    }
  }

  handleAppleLogin(): void {
    if (this.loading) return;
    if (typeof this.onAppleLogin === 'function') this.onAppleLogin();
  }

  handleGoogleLogin(): void {
    if (this.loading) return;
    if (typeof this.onGoogleLogin === 'function') this.onGoogleLogin();
  }

  handleForgotPassword(): void {
    if (typeof this.onForgotPassword === 'function') this.onForgotPassword();
  }

  handleRegister(): void {
    if (typeof this.onRegister === 'function') this.onRegister();
  }

  handleTerms(): void {
    if (typeof this.onTerms === 'function') this.onTerms();
  }

  handlePrivacy(): void {
    if (typeof this.onPrivacy === 'function') this.onPrivacy();
  }

  // ---- Getters ----

  get hasError(): boolean {
    return !!this.errorMessage;
  }

  get showSocial(): boolean {
    return !!this.showApple || !!this.showGoogle;
  }

  getAppleButtonClasses(): string {
    return BASE_CLASSES.socialButton + ' ' + BASE_CLASSES.apple;
  }

  getGoogleButtonClasses(): string {
    return BASE_CLASSES.socialButton + ' ' + BASE_CLASSES.google;
  }
}

export default LoginController;
